/* Vaultiqe — idle image warming ("never wait for an image")
 *
 * PROBLEM
 *   Below-fold / off-carousel product photos are `loading="lazy"`, which is
 *   right for first paint but means the fetch only STARTS when the user has
 *   already scrolled or swiped to them — so they visibly pop in late. On a
 *   phone that reads as "the site is still loading".
 *
 * WHAT THIS DOES
 *   Once the page has fully loaded (so nothing here can ever compete with the
 *   LCP image) and the main thread goes idle, it force-loads the remaining
 *   lazy product images at LOW priority, a few at a time. By the time the user
 *   swipes the PDP gallery or scrolls the collection grid, the bytes are
 *   already in cache and the image is instant.
 *
 * WHY IT'S SAFE FOR PERFORMANCE
 *   • Runs strictly AFTER window `load` + an idle callback — never during the
 *     critical path, so LCP/FCP are untouched.
 *   • fetchpriority="low" so it yields to anything the user actually triggers.
 *   • Hard concurrency cap — a burst of parallel downloads on a weak mobile
 *     connection is slower than a steady trickle and can stall interaction.
 *   • Opted out entirely on Save-Data and 2G/slow-2G: on those connections
 *     speculative bytes cost the user real money/time. They keep native lazy
 *     behaviour, which is the correct trade there.
 *   • prefers-reduced-data honoured for the same reason.
 *
 * ORDER
 *   The PDP gallery is warmed outward from the ACTIVE slide (next image first,
 *   then previous, then further out) because that matches swipe intent.
 *   Everything else is warmed in DOM order, which is top-to-bottom on a grid.
 */
(function () {
  'use strict';

  /* Classes covering every product photo that is lazy by default: the PDP
     gallery slides and the shared card image used by collection, homepage
     rails, PDP "travels well together", search, wishlist and recently-viewed. */
  var GALLERY = '.vpdp-main-img';
  var CARDS   = '.cb__card-img';

  /* Never run more than this many speculative fetches at once. Small on
     purpose: these are 200–400KB photos and the point is to fill cache
     quietly, not to saturate the connection. */
  var MAX_PARALLEL = 3;

  function saveDataMode() {
    var c = navigator.connection ||
            navigator.mozConnection ||
            navigator.webkitConnection;
    if (c) {
      if (c.saveData) return true;
      var t = c.effectiveType || '';
      if (t === 'slow-2g' || t === '2g') return true;
    }
    try {
      if (window.matchMedia('(prefers-reduced-data: reduce)').matches) return true;
    } catch (e) {}
    return false;
  }

  /* An <img> still worth warming: in the DOM, has a source, not already
     fetched. `complete` is true for both loaded and errored images, which is
     what we want — a broken image should not be retried here. */
  function pending(img) {
    if (!img || img.complete) return false;
    return !!(img.getAttribute('srcset') || img.getAttribute('src'));
  }

  function warm(img, done) {
    if (!pending(img)) { done(); return; }

    /* Low priority so a user-initiated fetch (tapping a product, opening the
       lightbox) always wins the connection. */
    try { img.setAttribute('fetchpriority', 'low'); } catch (e) {}

    /* Guarded so the slot is released EXACTLY once — the safety timeout below
       would otherwise fire after a normal load and decrement the in-flight
       counter twice, letting the queue exceed MAX_PARALLEL. */
    var released = false;
    var finish = function () {
      if (released) return;
      released = true;
      img.removeEventListener('load', finish);
      img.removeEventListener('error', finish);
      done();
    };
    img.addEventListener('load', finish);
    img.addEventListener('error', finish);

    /* Flipping the attribute is what actually releases a lazy image. Some
       engines won't re-evaluate on the flag alone, so re-assert the source to
       nudge them — assigning the identical value is a no-op when it already
       started, so this can't double-fetch. */
    img.loading = 'eager';
    var ss = img.getAttribute('srcset');
    if (ss) img.setAttribute('srcset', ss);
    var src = img.getAttribute('src');
    if (src) img.setAttribute('src', src);

    /* Safety net: if neither event fires (cached mid-flight, engine quirk),
       release the slot so the queue can never deadlock. */
    setTimeout(function () { finish(); }, 10000);
  }

  /* Warm `list` sequentially with a bounded number of parallel fetches. */
  function drain(list) {
    var i = 0;
    var active = 0;

    function next() {
      while (active < MAX_PARALLEL && i < list.length) {
        var img = list[i++];
        if (!pending(img)) continue;
        active++;
        warm(img, function () {
          active--;
          next();
        });
      }
    }
    next();
  }

  /* PDP gallery ordered outward from the active slide: the next image is the
     one a swipe reaches first, so it should be in cache first. */
  function galleryInSwipeOrder() {
    var imgs = [].slice.call(document.querySelectorAll(GALLERY));
    if (imgs.length < 2) return imgs;

    var active = 0;
    for (var n = 0; n < imgs.length; n++) {
      if (imgs[n].classList.contains('vpdp-main-img--active')) { active = n; break; }
    }

    var out = [];
    for (var d = 1; d < imgs.length; d++) {
      if (imgs[active + d]) out.push(imgs[active + d]);
      if (imgs[active - d]) out.push(imgs[active - d]);
    }
    return out;
  }

  function run() {
    if (saveDataMode()) return;

    var queue = galleryInSwipeOrder()
      .concat([].slice.call(document.querySelectorAll(CARDS)))
      .filter(pending);

    if (queue.length) drain(queue);
  }

  function schedule() {
    /* Idle callback keeps this off the main thread while the page is still
       settling (hydration, reveal observers, font swap). The timeout is the
       fallback for engines without requestIdleCallback (Safari < 17). */
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 1200);
    }
  }

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
})();
